import { teamsService } from "@/services/teamsService";
import { toast } from "sonner";

export interface TeamAnnouncement {
  id: string;
  team_id: string;
  author_id: string;
  title: string;
  content: string;
  type: "info" | "celebration" | "urgent";
  pinned: boolean;
  created_at: string;
  updated_at: string;
  last_edited_by?: string;
  last_edited_at?: string;
  edit_count: number;
  author?: {
    display_name: string;
    avatar_url?: string;
  };
  last_editor?: {
    display_name: string;
    avatar_url?: string;
  };
}

export interface TeamGoal {
  id: string;
  team_id: string;
  created_by: string;
  title: string;
  description: string;
  progress: number;
  status: "active" | "completed" | "paused";
  due_date: string;
  assigned_to: string[];
  created_at: string;
  updated_at: string;
  last_edited_by?: string;
  last_edited_at?: string;
  edit_count: number;
  creator?: {
    display_name: string;
    avatar_url?: string;
  };
  last_editor?: {
    display_name: string;
    avatar_url?: string;
  };
}

export interface TeamEvent {
  id: string;
  team_id: string;
  created_by: string;
  title: string;
  description?: string;
  event_date: string;
  event_type: "meeting" | "deadline" | "celebration" | "other";
  created_at: string;
}

export interface TeamFeedback {
  id: string;
  team_id: string;
  content: string;
  status: "new" | "acknowledged" | "addressed";
  created_at: string;
}

/**
 * Fetch team announcements
 */
export const fetchTeamAnnouncements = async (
  teamId: string
): Promise<TeamAnnouncement[]> => {
  try {
    const response = await teamsService.getAnnouncements(teamId);

    if (!response.success) {
      console.error("Error fetching announcements:", response.error);
      return [];
    }

    return response.data || [];
  } catch (error) {
    console.error("Failed to fetch announcements:", error);
    return [];
  }
};

/**
 * Create a new announcement
 */
export const createTeamAnnouncement = async (
  teamId: string,
  authorId: string,
  announcement: Omit<TeamAnnouncement, "id" | "team_id" | "author_id" | "created_at" | "updated_at">
): Promise<TeamAnnouncement | null> => {
  try {
    const response = await teamsService.createAnnouncement(teamId, {
      ...announcement,
      author_id: authorId,
    });

    if (!response.success) {
      console.error("Error creating announcement:", response.error);
      toast.error("Failed to create announcement");
      return null;
    }

    toast.success("Announcement created successfully!");
    return response.data;
  } catch (error) {
    console.error("Failed to create announcement:", error);
    toast.error("Failed to create announcement");
    return null;
  }
};

/**
 * Toggle announcement pin status
 */
export const toggleAnnouncementPin = async (
  teamId: string,
  announcementId: string,
  currentPinned: boolean
): Promise<boolean> => {
  try {
    const response = await teamsService.updateAnnouncement(teamId, announcementId, {
      pinned: !currentPinned,
    });

    if (!response.success) {
      console.error("Error toggling pin:", response.error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Failed to toggle pin:", error);
    return false;
  }
};

/**
 * Update announcement
 */
export const updateTeamAnnouncement = async (
  teamId: string,
  announcementId: string,
  updates: Partial<Pick<TeamAnnouncement, "title" | "content" | "type">>,
  editorId: string
): Promise<TeamAnnouncement | null> => {
  try {
    const response = await teamsService.updateAnnouncement(teamId, announcementId, updates);

    if (!response.success) {
      console.error("Error updating announcement:", response.error);
      toast.error("Failed to update announcement");
      return null;
    }

    toast.success("Announcement updated successfully!");
    return response.data;
  } catch (error) {
    console.error("Failed to update announcement:", error);
    toast.error("Failed to update announcement");
    return null;
  }
};

/**
 * Delete announcement
 */
export const deleteTeamAnnouncement = async (
  teamId: string,
  announcementId: string
): Promise<boolean> => {
  try {
    await teamsService.deleteAnnouncement(teamId, announcementId);
    toast.success("Announcement deleted successfully!");
    return true;
  } catch (error) {
    console.error("Failed to delete announcement:", error);
    toast.error("Failed to delete announcement");
    return false;
  }
};

/**
 * Fetch team goals
 */
export const fetchTeamGoals = async (
  teamId: string
): Promise<TeamGoal[]> => {
  try {
    const response = await teamsService.getGoals(teamId);

    if (!response.success) {
      console.error("Error fetching goals:", response.error);
      return [];
    }

    return response.data || [];
  } catch (error) {
    console.error("Failed to fetch goals:", error);
    return [];
  }
};

/**
 * Create a new team goal
 */
export const createTeamGoal = async (
  teamId: string,
  createdBy: string,
  goal: Omit<TeamGoal, "id" | "team_id" | "created_by" | "created_at" | "updated_at">
): Promise<TeamGoal | null> => {
  try {
    const response = await teamsService.createGoal(teamId, {
      ...goal,
      created_by: createdBy,
    });

    if (!response.success) {
      console.error("Error creating goal:", response.error);
      toast.error("Failed to create goal");
      return null;
    }

    toast.success("Goal created successfully!");
    return response.data;
  } catch (error) {
    console.error("Failed to create goal:", error);
    toast.error("Failed to create goal");
    return null;
  }
};

/**
 * Update goal progress
 */
export const updateGoalProgress = async (
  teamId: string,
  goalId: string,
  progress: number,
  notes?: string
): Promise<boolean> => {
  try {
    const response = await teamsService.updateGoal(teamId, goalId, {
      progress,
      notes,
    });

    if (!response.success) {
      console.error("Error updating goal progress:", response.error);
      toast.error("Failed to update goal progress");
      return false;
    }

    return true;
  } catch (error) {
    console.error("Failed to update goal progress:", error);
    toast.error("Failed to update goal progress");
    return false;
  }
};

/**
 * Update team goal
 */
export const updateTeamGoal = async (
  teamId: string,
  goalId: string,
  updates: Partial<Pick<TeamGoal, "title" | "description" | "status" | "due_date" | "progress">>,
  editorId: string
): Promise<TeamGoal | null> => {
  try {
    const response = await teamsService.updateGoal(teamId, goalId, updates);

    if (!response.success) {
      console.error("Error updating goal:", response.error);
      toast.error("Failed to update goal");
      return null;
    }

    toast.success("Goal updated successfully!");
    return response.data;
  } catch (error) {
    console.error("Failed to update goal:", error);
    toast.error("Failed to update goal");
    return null;
  }
};

/**
 * Delete team goal
 */
export const deleteTeamGoal = async (
  teamId: string,
  goalId: string
): Promise<boolean> => {
  try {
    await teamsService.deleteGoal(teamId, goalId);
    toast.success("Goal deleted successfully!");
    return true;
  } catch (error) {
    console.error("Failed to delete goal:", error);
    toast.error("Failed to delete goal");
    return false;
  }
};

/**
 * Fetch team events
 */
export const fetchTeamEvents = async (
  teamId: string
): Promise<TeamEvent[]> => {
  try {
    const response = await teamsService.getEvents(teamId);

    if (!response.success) {
      console.error("Error fetching events:", response.error);
      return [];
    }

    return response.data || [];
  } catch (error) {
    console.error("Failed to fetch events:", error);
    return [];
  }
};

/**
 * Create team event
 */
export const createTeamEvent = async (
  teamId: string,
  event: Omit<TeamEvent, "id" | "created_at">
): Promise<TeamEvent | null> => {
  try {
    const response = await teamsService.createEvent(teamId, event);

    if (!response.success) {
      console.error("Error creating event:", response.error);
      toast.error("Failed to create event");
      return null;
    }

    toast.success("Event created successfully!");
    return response.data;
  } catch (error) {
    console.error("Failed to create event:", error);
    toast.error("Failed to create event");
    return null;
  }
};

/**
 * Submit anonymous feedback
 */
export const submitAnonymousFeedback = async (
  teamId: string,
  content: string
): Promise<boolean> => {
  try {
    const response = await teamsService.submitFeedback(teamId, {
      content,
      anonymous: true,
    });

    if (!response.success) {
      console.error("Error submitting feedback:", response.error);
      toast.error("Failed to submit feedback");
      return false;
    }

    toast.success("Feedback submitted successfully!");
    return true;
  } catch (error) {
    console.error("Failed to submit feedback:", error);
    toast.error("Failed to submit feedback");
    return false;
  }
};

/**
 * Fetch team feedback
 */
export const fetchTeamFeedback = async (
  teamId: string
): Promise<TeamFeedback[]> => {
  try {
    const response = await teamsService.getFeedback(teamId);

    if (!response.success) {
      console.error("Error fetching feedback:", response.error);
      return [];
    }

    return response.data || [];
  } catch (error) {
    console.error("Failed to fetch feedback:", error);
    return [];
  }
};

/**
 * Update feedback status
 */
export const updateFeedbackStatus = async (
  teamId: string,
  feedbackId: string,
  status: "new" | "acknowledged" | "addressed"
): Promise<boolean> => {
  try {
    // This would need a new endpoint: PUT /teams/:teamId/feedback/:feedbackId
    const response = await teamsService.submitFeedback(teamId, {
      feedbackId,
      status,
    });

    if (!response.success) {
      console.error("Error updating feedback status:", response.error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Failed to update feedback status:", error);
    return false;
  }
};
